import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import {
  Users, GraduationCap, Layers, BookOpen, Newspaper, DollarSign, TrendingUp,
  ArrowRight, Settings, Award, MessageCircle, ShoppingBag, Heart, ClipboardList,
  Clock, Star, FileText, Home, Bookmark, Edit2, Plus, Trash2,
  ChevronUp, ChevronDown, X, Check, Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_MAP = {
  Users, GraduationCap, Layers, BookOpen, Newspaper, DollarSign, TrendingUp,
  Settings, Award, MessageCircle, ShoppingBag, Heart, ClipboardList,
  Clock, Star, FileText, Home, Bookmark
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

const DEFAULT_LINKS = [
  { label: 'Data Santri', path: '/admin/santri', icon: 'Users' },
  { label: 'Data Guru', path: '/admin/guru', icon: 'GraduationCap' },
  { label: 'Halaqah', path: '/admin/halaqah', icon: 'Layers' },
  { label: 'BQ News', path: '/admin/berita', icon: 'Newspaper' },
  { label: 'Toko ZahBaq', path: '/admin/toko', icon: 'ShoppingBag' },
  { label: 'Pengaturan', path: '/admin/settings', icon: 'Settings' },
];

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="bg-white rounded-2xl p-3 md:p-5 shadow-card border border-gray-50"
    >
      <div className={`w-9 h-9 md:w-11 md:h-11 rounded-xl ${color} flex items-center justify-center mb-2 md:mb-3`}>
        <Icon size={17} className="text-white" />
      </div>
      <p className="text-xl md:text-3xl font-bold text-gray-800 mb-0.5 md:mb-1">{value ?? '—'}</p>
      <p className="text-xs md:text-sm text-gray-500">{label}</p>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [quickLinks, setQuickLinks] = useState(DEFAULT_LINKS);
  const [editMode, setEditMode] = useState(false);
  const [editLinks, setEditLinks] = useState([]);
  const [newLink, setNewLink] = useState({ label: '', path: '', icon: 'Users' });
  const [showAddForm, setShowAddForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/stats`).then(({ data }) => setStats(data)).catch(() => {});
    axios.get(`${API}/settings`).then(({ data }) => {
      setSettings(data);
      if (data.dashboard_quick_links) {
        try {
          const parsed = JSON.parse(data.dashboard_quick_links);
          if (Array.isArray(parsed) && parsed.length > 0) setQuickLinks(parsed);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  const startEdit = () => {
    setEditLinks([...quickLinks]);
    setShowAddForm(false);
    setNewLink({ label: '', path: '', icon: 'Users' });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setShowAddForm(false);
  };

  const saveEdit = async () => {
    if (editLinks.length === 0) { toast.error('Minimal harus ada 1 ikon'); return; }
    try {
      await axios.post(`${API}/settings`, { dashboard_quick_links: JSON.stringify(editLinks) });
      setQuickLinks([...editLinks]);
      setEditMode(false);
      setShowAddForm(false);
      toast.success('Ikon dashboard berhasil disimpan!');
    } catch {
      toast.error('Gagal menyimpan konfigurasi');
    }
  };

  const moveUp = (i) => {
    if (i === 0) return;
    const arr = [...editLinks];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setEditLinks(arr);
  };

  const moveDown = (i) => {
    if (i === editLinks.length - 1) return;
    const arr = [...editLinks];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setEditLinks(arr);
  };

  const removeLink = (i) => {
    setEditLinks(editLinks.filter((_, idx) => idx !== i));
  };

  const addLink = () => {
    if (!newLink.label.trim() || !newLink.path.trim()) {
      toast.error('Label dan path wajib diisi'); return;
    }
    setEditLinks([...editLinks, { ...newLink }]);
    setNewLink({ label: '', path: '', icon: 'Users' });
    setShowAddForm(false);
  };

  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6">
          <div className="flex items-start md:items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-playfair text-2xl font-bold text-emerald-950">Dashboard Admin</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {settings.tahun_ajaran} · Semester {settings.semester}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Pesantren</p>
              <p className="text-sm font-semibold text-emerald-700">{settings.nama_pesantren || 'Baitul Quran'}</p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            <StatCard icon={Users} label="Total Santri" value={stats.total_santri} color="bg-emerald-500" delay={0.1} />
            <StatCard icon={GraduationCap} label="Total Guru" value={stats.total_guru} color="bg-blue-500" delay={0.15} />
            <StatCard icon={Layers} label="Total Halaqah" value={stats.total_halaqah} color="bg-purple-500" delay={0.2} />
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
            <StatCard icon={Newspaper} label="Total Berita" value={stats.total_berita} color="bg-orange-500" delay={0.3} />
            <StatCard icon={DollarSign} label="Total Donasi" value={stats.total_donasi !== undefined ? rupiah(stats.total_donasi) : '—'} color="bg-teal-500" delay={0.35} />
          </div>

          {/* Quick Links */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-700">Akses Cepat</h2>
              {!editMode ? (
                <button
                  data-testid="edit-quick-links-btn"
                  onClick={startEdit}
                  className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-xl transition-all"
                >
                  <Edit2 size={12} /> Edit Ikon
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    data-testid="cancel-edit-links-btn"
                    onClick={cancelEdit}
                    className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-xl transition-all"
                  >
                    <X size={12} /> Batal
                  </button>
                  <button
                    data-testid="save-edit-links-btn"
                    onClick={saveEdit}
                    className="flex items-center gap-1.5 text-xs text-white bg-emerald-500 hover:bg-emerald-600 px-3 py-1.5 rounded-xl transition-all"
                  >
                    <Save size={12} /> Simpan
                  </button>
                </div>
              )}
            </div>

            {editMode ? (
              <div className="bg-white rounded-2xl shadow-card border border-gray-50 p-4 space-y-2">
                {editLinks.map((link, i) => {
                  const Icon = ICON_MAP[link.icon] || BookOpen;
                  return (
                    <div
                      key={i}
                      data-testid={`edit-link-item-${i}`}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <Icon size={15} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-700 truncate">{link.label}</p>
                        <p className="text-xs text-gray-400 truncate">{link.path}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => moveUp(i)}
                          disabled={i === 0}
                          data-testid={`move-up-${i}`}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 disabled:opacity-30 transition-colors"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          onClick={() => moveDown(i)}
                          disabled={i === editLinks.length - 1}
                          data-testid={`move-down-${i}`}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 disabled:opacity-30 transition-colors"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          onClick={() => removeLink(i)}
                          data-testid={`remove-link-${i}`}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {editLinks.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-4">Belum ada ikon. Tambahkan di bawah.</p>
                )}

                <AnimatePresence>
                  {showAddForm ? (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="border-2 border-dashed border-emerald-200 rounded-xl p-4 space-y-3 bg-emerald-50/30"
                    >
                      <p className="text-xs font-semibold text-emerald-700">Tambah Ikon Baru</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Label</label>
                          <input
                            data-testid="new-link-label"
                            value={newLink.label}
                            onChange={e => setNewLink(n => ({ ...n, label: e.target.value }))}
                            placeholder="Data Santri"
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Path</label>
                          <input
                            data-testid="new-link-path"
                            value={newLink.path}
                            onChange={e => setNewLink(n => ({ ...n, path: e.target.value }))}
                            placeholder="/admin/santri"
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1.5">Pilih Ikon</label>
                        <div className="flex flex-wrap gap-1.5">
                          {ICON_OPTIONS.map(name => {
                            const Ic = ICON_MAP[name];
                            return (
                              <button
                                key={name}
                                type="button"
                                onClick={() => setNewLink(n => ({ ...n, icon: name }))}
                                data-testid={`icon-option-${name}`}
                                title={name}
                                className={`p-2 rounded-lg flex items-center justify-center transition-all ${
                                  newLink.icon === name
                                    ? 'bg-emerald-500 text-white shadow-sm'
                                    : 'bg-white border border-gray-200 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600'
                                }`}
                              >
                                <Ic size={14} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowAddForm(false)}
                          className="flex-1 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Batal
                        </button>
                        <button
                          type="button"
                          onClick={addLink}
                          data-testid="confirm-add-link"
                          className="flex-1 py-2 bg-emerald-500 text-white rounded-xl text-xs font-semibold flex items-center justify-center gap-1 hover:bg-emerald-600"
                        >
                          <Check size={12} /> Tambahkan
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <button
                      data-testid="add-link-btn"
                      onClick={() => setShowAddForm(true)}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-emerald-200 rounded-xl text-sm text-emerald-600 hover:bg-emerald-50 transition-all"
                    >
                      <Plus size={14} /> Tambah Ikon
                    </button>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {quickLinks.map((link, idx) => {
                  const Icon = ICON_MAP[link.icon] || BookOpen;
                  return (
                    <button
                      key={`${link.path}-${idx}`}
                      data-testid={`quick-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => navigate(link.path)}
                      className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-card hover:shadow-emerald hover:-translate-y-0.5 transition-all duration-200 border border-gray-50 text-left group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                        <Icon size={17} className="text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-700">{link.label}</p>
                      </div>
                      <ArrowRight size={14} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
